import { cn } from "@/lib/cn";

export interface LegendItem {
  key: string;
  label: string;
  /** A `chart-tone--*` suffix: `accent`, `ok`, `stop`, `cat-3`, `neutral`, … */
  tone: string;
}

/**
 * Which series is which, under a chart that has more than one.
 *
 * Names only, no figures: every chart that mounts this also carries a tooltip
 * and an `aria-label` with the numbers in them, and a legend that repeats them
 * is a second place for a total to be wrong. The donut is the exception and does
 * not use this - it draws its own rows, because on a ring the legend *is* the
 * table and the picture is the ring.
 *
 * ── One swatch shape, and it is a square ───────────────────────────────────
 *
 * There used to be four - a bar, a dot, a rule and a dashed rule - on the
 * argument that a swatch should be drawn as the mark it stands for, so a reader
 * would not have to translate between the key and the figure.
 *
 * It does not pay for itself. The swatch is 10px: at that size a 2px rule and a
 * 2px dashed rule are the same grey smudge, so the shape that was supposed to
 * disambiguate two series was the least legible thing about them, and a legend
 * whose rows are different *heights* reads as three kinds of object rather than
 * one list. What tells two series apart is hue in the key and hue on the
 * figure, and that works at any size. The square is the shape that carries a
 * hue best, so every row gets one and the rows line up.
 */
export function ChartLegend({ items, className }: { items: LegendItem[]; className?: string }) {
  return (
    <ul className={cn("legend", className)}>
      {items.map((item) => (
        <li key={item.key} className={cn("legend__item", `chart-tone--${item.tone}`)}>
          <i className="legend__swatch" aria-hidden />
          <span className="legend__label">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}
